/**
 * Bulk-edit overrides for a set of selected rooms.
 *
 * Use case: an owner (or a lessor on the inheritance chain) owns
 * several rooms in the same property — typically the rooms that make up
 * one apartment. Without this dialog, an editor has to open each room's
 * edit sheet and copy the same lessor / bank / rep override into each.
 * This is the stopgap until we add a proper Apartment layer; once
 * apartments exist, the inheritance moves up a level and bulk-edit
 * collapses into "edit this apartment."
 *
 * Three sections, each independently togglable:
 *   1. Lessor override — pick a lessor row; the room's lessor_override
 *      JSONB gets { lessor_id } set
 *   2. Representor override — single Representor field + Note (matches
 *      the consolidated single-field shape from the prior representor
 *      PR); written to the same lessor_override JSONB as { rep_name,
 *      rep_note }
 *   3. Bank override — holder, IBAN, BIC, bank name; upserts to
 *      room_bank_overrides
 *
 * Each section can also be set to "clear" (write null) so editors can
 * wipe overrides on a batch and fall back to the property/lessor
 * defaults in one action.
 *
 * Apply runs the per-room mutations sequentially so a partial failure
 * shows a clear "M succeeded, K failed" toast and the editor knows
 * which rooms still need attention.
 */
import { useEffect, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LessorFormDialog } from "@/components/admin/v2/LessorFormDialog";
import { useCreateLessor, useLessors } from "@/hooks/v2/useLessors";
import {
  useUpdateRoom,
  useUpsertRoomBankOverride,
  useDeleteRoomBankOverride,
} from "@/hooks/v2/useProperties";
import type { LessorInput } from "@/types/lessor";
import type { Room } from "@/types/property";
import { useLanguage } from "@/contexts/LanguageContext";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rooms: Room[];
  /** Notify parent (RoomsTab) so it can clear its selection. */
  onApplied: () => void;
};

/**
 * Each section's state is independent. `enabled` gates whether the
 * section sends an update at all; `clear` (only meaningful when
 * enabled) means "write null to wipe the override" instead of writing
 * a populated value.
 */
type SectionState = {
  enabled: boolean;
  clear: boolean;
};

export function BulkEditRoomsDialog({
  open,
  onOpenChange,
  rooms,
  onApplied,
}: Props) {
  const { toast } = useToast();
  const { t } = useLanguage();
  const { data: lessors } = useLessors();

  const updateRoomMut = useUpdateRoom();
  const bankUpsertMut = useUpsertRoomBankOverride();
  const bankDeleteMut = useDeleteRoomBankOverride();
  const lessorCreateMut = useCreateLessor();

  // ─── Section toggles ─────────────────────────────────────────────────
  const [lessorSection, setLessorSection] = useState<SectionState>({
    enabled: false,
    clear: false,
  });
  const [repSection, setRepSection] = useState<SectionState>({
    enabled: false,
    clear: false,
  });
  const [bankSection, setBankSection] = useState<SectionState>({
    enabled: false,
    clear: false,
  });

  // ─── Section values ──────────────────────────────────────────────────
  const [lessorId, setLessorId] = useState<string | null>(null);
  const [repName, setRepName] = useState("");
  const [repNote, setRepNote] = useState("");
  const [bankHolder, setBankHolder] = useState("");
  const [bankIban, setBankIban] = useState("");
  const [bankBic, setBankBic] = useState("");
  const [bankName, setBankName] = useState("");

  // Inline lessor add (mirrors PropertyEditSheet's pattern)
  const [lessorDialogOpen, setLessorDialogOpen] = useState(false);

  // Apply progress for the toast
  const [isApplying, setIsApplying] = useState(false);

  // Reset whenever the dialog opens fresh
  useEffect(() => {
    if (open) {
      setLessorSection({ enabled: false, clear: false });
      setRepSection({ enabled: false, clear: false });
      setBankSection({ enabled: false, clear: false });
      setLessorId(null);
      setRepName("");
      setRepNote("");
      setBankHolder("");
      setBankIban("");
      setBankBic("");
      setBankName("");
    }
  }, [open]);

  function handleLessorCreated(input: LessorInput) {
    lessorCreateMut.mutate(input, {
      onSuccess: (lessor) => {
        toast({
          title: t("admin.apartments.ownerAdded", "Owner added"),
          description: lessor.name,
        });
        setLessorId(lessor.id);
        setLessorDialogOpen(false);
      },
      onError: (err) => {
        toast({
          title: t("admin.rooms.couldNotAddLessor", "Could not add lessor"),
          description:
            err instanceof Error
              ? err.message
              : t("common.unknownError", "Unknown error"),
          variant: "destructive",
        });
      },
    });
  }

  /**
   * Build the lessor_override JSONB payload for a single room, given
   * the dialog's current state. Returns:
   *   - undefined → don't touch lessor_override at all
   *   - null → write null (clear override)
   *   - object → write the populated override
   *
   * The "undefined vs null" distinction matters because rooms have
   * separate sections (lessor / rep) that share one JSONB column. If
   * only "rep" is being edited, we should preserve the existing
   * lessor_id; we merge with the room's current lessor_override below.
   */
  function buildLessorOverride(room: Room): unknown | undefined {
    const lessorEnabled = lessorSection.enabled;
    const repEnabled = repSection.enabled;
    if (!lessorEnabled && !repEnabled) return undefined;

    // Both sections set to "clear" → write null and wipe the column.
    if (
      lessorEnabled &&
      lessorSection.clear &&
      repEnabled &&
      repSection.clear
    ) {
      return null;
    }

    // Otherwise merge: start from the existing override, then overwrite
    // the fields the editor opted into.
    const existing = (room.lessor_override ?? {}) as Record<string, unknown>;
    const next: Record<string, unknown> = { ...existing };

    if (lessorEnabled) {
      if (lessorSection.clear) {
        delete next.lessor_id;
      } else {
        next.lessor_id = lessorId;
      }
    }
    if (repEnabled) {
      if (repSection.clear) {
        delete next.rep_name;
        delete next.rep_note;
        // Also drop legacy `rep_role` if it's lying around in this row.
        delete next.rep_role;
      } else {
        next.rep_name = repName.trim() || null;
        next.rep_note = repNote.trim() || null;
        delete next.rep_role;
      }
    }

    // If the merged object has no meaningful keys, write null instead
    // of an empty object — keeps the column clean.
    const meaningful = Object.entries(next).some(
      ([, v]) => v != null && v !== ""
    );
    return meaningful ? next : null;
  }

  async function handleApply() {
    const enabledCount =
      Number(lessorSection.enabled) +
      Number(repSection.enabled) +
      Number(bankSection.enabled);
    if (enabledCount === 0) {
      toast({
        title: t("admin.rooms.nothingToApply", "Nothing to apply"),
        description: t(
          "admin.rooms.toggleAtLeastOneSection",
          "Toggle at least one section before applying."
        ),
      });
      return;
    }

    if (lessorSection.enabled && !lessorSection.clear && !lessorId) {
      toast({
        title: t("admin.rooms.pickAnOwner", "Pick an owner"),
        description: t(
          "admin.rooms.orToggleClearLessor",
          'Or toggle "Clear" to remove the lessor override.'
        ),
        variant: "destructive",
      });
      return;
    }
    if (bankSection.enabled && !bankSection.clear && !bankIban.trim()) {
      toast({
        title: t("admin.rooms.ibanRequired", "IBAN required"),
        description: t(
          "admin.rooms.orToggleClearBank",
          'Or toggle "Clear" to remove the bank override.'
        ),
        variant: "destructive",
      });
      return;
    }

    setIsApplying(true);
    let succeeded = 0;
    const failures: string[] = [];

    for (const room of rooms) {
      try {
        // 1. Lessor / rep override — combined JSONB
        const lessorPayload = buildLessorOverride(room);
        if (lessorPayload !== undefined) {
          await updateRoomMut.mutateAsync({
            id: room.id,
            propertyId: room.property_id,
            // Cast: lessor_override is JSONB; the resolver in
            // useProperties.ts tolerates either {name, note} or legacy
            // {name, role, note} shapes.
            patch: { lessor_override: lessorPayload } as Partial<Room>,
          });
        }

        // 2. Bank override — separate table
        if (bankSection.enabled) {
          if (bankSection.clear) {
            await bankDeleteMut.mutateAsync(room.id);
          } else {
            await bankUpsertMut.mutateAsync({
              room_id: room.id,
              holder: bankHolder.trim() || null,
              iban: bankIban.trim().toUpperCase(),
              bic: bankBic.trim().toUpperCase() || null,
              bank_name: bankName.trim() || null,
            });
          }
        }

        succeeded += 1;
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : t("common.unknownError", "Unknown error");
        failures.push(`${room.title}: ${msg}`);
      }
    }

    setIsApplying(false);

    if (failures.length === 0) {
      toast({
        title: t("admin.rooms.bulkEditApplied", "Bulk edit applied"),
        description: t(
          "admin.rooms.roomsUpdatedCount",
          "{{count}} room{{plural}} updated."
        )
          .replace("{{count}}", String(succeeded))
          .replace("{{plural}}", succeeded === 1 ? "" : "s"),
      });
      onApplied();
      onOpenChange(false);
    } else {
      toast({
        title: t(
          "admin.rooms.succeededFailedCount",
          "{{succeeded}} succeeded, {{failed}} failed"
        )
          .replace("{{succeeded}}", String(succeeded))
          .replace("{{failed}}", String(failures.length)),
        description: failures.slice(0, 3).join(" · "),
        variant: "destructive",
      });
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {t("common.edit", "Edit")} {rooms.length}{" "}
              {rooms.length === 1
                ? t("admin.rooms.roomNoun", "room")
                : t("admin.rooms.roomsNoun", "rooms")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "admin.rooms.bulkEditDesc",
                'Toggle a section to override it on every selected room. Toggle "Clear" to wipe the override and fall back to property / lessor defaults instead.'
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* ─── Lessor section ─── */}
            <Section
              title={t("admin.field.owner", "Owner")}
              description={t(
                "admin.rooms.pickLessorForRooms",
                "Pick a different lessor for these rooms (e.g. an owner who owns this apartment)."
              )}
              section={lessorSection}
              onChange={setLessorSection}
            >
              {!lessorSection.clear && (
                <div className="flex items-stretch gap-2">
                  <Select
                    value={lessorId ?? ""}
                    onValueChange={(v) => setLessorId(v || null)}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue
                        placeholder={t(
                          "admin.rooms.pickAnOwnerPlaceholder",
                          "Pick an owner…"
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {(lessors ?? []).map((l) => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setLessorDialogOpen(true)}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    {t("admin.rooms.new", "New")}
                  </Button>
                </div>
              )}
            </Section>

            {/* ─── Representor section ─── */}
            <Section
              title={t("admin.apartments.representor", "Representor")}
              description={t(
                "admin.rooms.representorOverrideDesc",
                'Goes after "vertreten durch" on contracts. Overrides whatever the lessor would otherwise contribute.'
              )}
              section={repSection}
              onChange={setRepSection}
            >
              {!repSection.clear && (
                <div className="space-y-2">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">
                      {t("admin.apartments.representor", "Representor")}
                    </Label>
                    <Input
                      value={repName}
                      onChange={(e) => setRepName(e.target.value)}
                      placeholder={t(
                        "admin.apartments.representorPlaceholder",
                        'e.g. "den Geschäftsführer" or "Coliviq GmbH"'
                      )}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">
                      {t("admin.rooms.note", "Note")}{" "}
                      <span className="text-muted-foreground font-normal">
                        ({t("admin.rooms.optional", "optional")})
                      </span>
                    </Label>
                    <Textarea
                      value={repNote}
                      onChange={(e) => setRepNote(e.target.value)}
                      rows={2}
                      placeholder={t(
                        "admin.rooms.repNotePlaceholder",
                        "e.g. vertreten durch Coliviq GmbH"
                      )}
                    />
                  </div>
                </div>
              )}
            </Section>

            {/* ─── Bank section ─── */}
            <Section
              title={t("admin.apartments.bankAccount", "Bank account")}
              description={t(
                "admin.rooms.bankAccountOverrideDesc",
                "Where rent for these rooms is paid. Overrides the lessor's default."
              )}
              section={bankSection}
              onChange={setBankSection}
            >
              {!bankSection.clear && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">
                      {t("admin.properties.accountHolder", "Account holder")}
                    </Label>
                    <Input
                      value={bankHolder}
                      onChange={(e) => setBankHolder(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">
                      {t("admin.properties.iban", "IBAN")}
                    </Label>
                    <Input
                      value={bankIban}
                      onChange={(e) => setBankIban(e.target.value)}
                      placeholder="DE89 …"
                      autoComplete="off"
                      spellCheck={false}
                      className="font-mono uppercase"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">
                      {t("admin.properties.bic", "BIC")}
                    </Label>
                    <Input
                      value={bankBic}
                      onChange={(e) => setBankBic(e.target.value)}
                      autoComplete="off"
                      spellCheck={false}
                      className="font-mono uppercase"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">
                      {t("admin.properties.bankName", "Bank name")}
                    </Label>
                    <Input
                      value={bankName}
                      onChange={(e) => setBankName(e.target.value)}
                      placeholder="Commerzbank"
                    />
                  </div>
                </div>
              )}
            </Section>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={isApplying}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              type="button"
              onClick={() => void handleApply()}
              disabled={isApplying}
            >
              {isApplying ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t("admin.rooms.applying", "Applying…")}
                </>
              ) : (
                `${t("admin.rooms.applyTo", "Apply to")} ${rooms.length} ${
                  rooms.length === 1
                    ? t("admin.rooms.roomNoun", "room")
                    : t("admin.rooms.roomsNoun", "rooms")
                }`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inline lessor add — same pattern as PropertyEditSheet. */}
      <LessorFormDialog
        open={lessorDialogOpen}
        onOpenChange={setLessorDialogOpen}
        mode="create"
        isSubmitting={lessorCreateMut.isPending}
        onSubmit={handleLessorCreated}
      />
    </>
  );
}

// ─── Local helpers ─────────────────────────────────────────────────────

function Section({
  title,
  description,
  section,
  onChange,
  children,
}: {
  title: string;
  description: string;
  section: SectionState;
  onChange: (next: SectionState) => void;
  children: React.ReactNode;
}) {
  const { t } = useLanguage();
  return (
    <section className="rounded-lg border bg-muted/30 px-4 py-3 space-y-3">
      <header className="flex items-start justify-between gap-3">
        <div className="space-y-0.5">
          <h3 className="text-sm font-semibold">{title}</h3>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
        <Switch
          checked={section.enabled}
          onCheckedChange={(v) =>
            onChange({ enabled: v, clear: v ? section.clear : false })
          }
        />
      </header>
      {section.enabled && (
        <>
          <div className="flex items-center gap-2">
            <Switch
              id={`${title}-clear`}
              checked={section.clear}
              onCheckedChange={(v) => onChange({ ...section, clear: v })}
            />
            <Label
              htmlFor={`${title}-clear`}
              className="text-xs cursor-pointer"
            >
              {t(
                "admin.rooms.clearOverrideHint",
                "Clear (remove the override on every selected room)"
              )}
            </Label>
          </div>
          {children}
        </>
      )}
    </section>
  );
}
